import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { environment } from '@lsq-frontend/fasttrack-shared/environment';
import { atlas, SETTINGS_PROFILE } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    DashboardClaim,
    Guid,
    ResponseCode,
    UserStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListPeopleDetailForClientRequest,
    ListPeopleDetailForClientService,
    PersonDetail
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    DateFormat,
    forLifeOf,
    LifecycleAware,
    Modal,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkUrlById,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { DashboardExperience } from '../../../constants/experience.constants';
import { ManageUserAction } from '../../../constants/user-settings.constants';
import { ManageUsersService } from '../../services/manage-users.service';
import { AddUserPermissionsModal, AddUserPersonalDetailsModal } from '../add-user/add-user-modal';
import { PermissionsModalComponent } from '../permissions/permissions-modal.component';

export interface IImpersonateUser {
    companyKey: string | Guid;
    userKey?: string;
}

@LifecycleAware()
@Component({
    selector: 'users',
    templateUrl: './users.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnChanges, OnInit, OnDestroy {
    @Input() public companyKey?: Guid;
    @Input() public isAdmin = false;
    @Input() public isProspectView? = false;
    @Input() public clientDashboardExperiences: DashboardExperience[] = [];
    @Input() public isLsqAdminCompany = false;
    @Output() public impersonateUser = new EventEmitter<IImpersonateUser>();

    public userDetailsStream: Observable<PersonDetail[]>;
    public isFetchingUserDetails = true;
    public tableAppearance = TableAppearance;
    public dateFormat = DateFormat;

    private _isUpdatingClaimsSubject = new BehaviorSubject<boolean>(false);
    private _listPeopleDetailForClientRequest: ListPeopleDetailForClientRequest;

    public showActiveUsers = false;
    public showInactiveUsers = false;
    public showPendingUsers = false;

    private _activeUserDetailsSubject = new BehaviorSubject<PersonDetail[]>([]);
    private _inactiveUserDetailsSubject = new BehaviorSubject<PersonDetail[]>([]);
    private _pendingUserDetailsSubject = new BehaviorSubject<PersonDetail[]>([]);

    public activeDataSource = new LsqListSource<PersonDetail>({
        rows$: this._activeUserDetailsSubject.asObservable()
    });
    public inactiveDataSource = new LsqListSource<PersonDetail>({
        rows$: this._inactiveUserDetailsSubject.asObservable()
    });
    public pendingDataSource = new LsqListSource<PersonDetail>({
        rows$: this._pendingUserDetailsSubject.asObservable()
    });

    public hasSystemAdminClaimManagement = this._claimService.isOnSync(
        DashboardClaim.SystemAdminClaimManagement
    );

    private _modalTitle = this._i18n({ value: 'Add a New User' });
    public reactivatePrefixDetailsText = this._i18n({
        value: 'Reactivate',
        meaning: "Part 1/2: Reactivate {firstName} {lastName}'s account"
    });
    public reactivateSuffixDetailsText = this._i18n({
        value: "'s account",
        meaning: "Part 2/2: Reactivate {firstName} {lastName}'s account"
    });
    public deactivatePrefixDetailsText = this._i18n({
        value: 'Deactivate',
        meaning: "Part 1/2: Deactivate {firstName} {lastName}'s account"
    });
    public deactivateSuffixDetailsText = this._i18n({
        value: "'s account",
        meaning: "Part 2/2: Deactivate {firstName} {lastName}'s account"
    });
    public deactivateTitle = this._i18n({
        value: 'Deactivate User'
    });
    public modifyPermissions = this._i18n({
        value: 'Modify User Permissions for',
        meaning:
            'Part of: Modify User Permissions for ${personDetail.firstName} ${personDetail.lastName}'
    });
    public updateSuccessMessage = this._i18n({
        value: 'The user was updated successfully.',
        meaning: 'User updated success message'
    });
    public atLabel = this._i18n({
        value: 'at',
        meaning: '"At" between date and time label'
    });

    constructor(
        private router: Router,
        private _modalService: ModalService,
        private _modalWorkflowService: ModalWorkflowService,
        private _alertService: AlertService,
        public userService: UserService,
        private _manageUsersService: ManageUsersService,
        private _listPeopleDetailForClientService: ListPeopleDetailForClientService,
        private _i18n: I18n,
        public responsiveService: ResponsiveService,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService
    ) {}

    public ngOnInit(): void {
        // Configure the request and service.
        this._listPeopleDetailForClientRequest = new ListPeopleDetailForClientRequest({
            companyKey: this.companyKey,
            take: -1
        });

        // Define the `userDetailsStream`.
        this.userDetailsStream = this._listPeopleDetailForClientService.dataStream.pipe(
            map((response) => response.rows),
            tap(() => (this.isFetchingUserDetails = false))
        );

        this.userDetailsStream.pipe(forLifeOf(this)).subscribe((personDetails) => {
            this._updateUsersTables(personDetails);
        });

        if (this.companyKey) {
            this._listPeopleDetailForClientService.execute(
                new ListPeopleDetailForClientRequest(this._listPeopleDetailForClientRequest)
            );
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const companyKey: SimpleChange = changes.companyKey;

        if (
            companyKey &&
            this._listPeopleDetailForClientRequest &&
            companyKey.currentValue !== companyKey.previousValue
        ) {
            this._listPeopleDetailForClientRequest.companyKey = companyKey.currentValue;
            this.isFetchingUserDetails = true;
            this._listPeopleDetailForClientService.execute(
                new ListPeopleDetailForClientRequest(this._listPeopleDetailForClientRequest)
            );
        }
    }

    public ngOnDestroy(): void {}

    private _updateUsersTables(personDetails: PersonDetail[]): void {
        const activeUsers = personDetails.filter(
            (personDetail) => personDetail.userStatus === UserStatus.Active
        );
        this.showActiveUsers = activeUsers.length > 0;
        this._activeUserDetailsSubject.next(activeUsers);

        const inactiveUsers = personDetails.filter(
            (personDetail) => personDetail.userStatus === UserStatus.Inactive
        );
        this.showInactiveUsers = inactiveUsers.length > 0;
        this._inactiveUserDetailsSubject.next(inactiveUsers);

        const pendingUsers = personDetails.filter(
            (personDetail) => personDetail.userStatus === UserStatus.Pending
        );
        this.showPendingUsers = pendingUsers.length > 0;
        this._pendingUserDetailsSubject.next(pendingUsers);
    }

    public async manageUser(personDetail: PersonDetail): Promise<void> {
        const currentUser = await this.userService.user$.pipe(first()).toPromise();

        if (personDetail.userKey !== currentUser.userKey) {
            this._modalService.showModal(
                new Modal({
                    id: 'modify-user-permissions',
                    title: `${this.modifyPermissions} ${personDetail.firstName} ${personDetail.lastName}`,
                    component: PermissionsModalComponent,
                    width: '65.5rem',
                    showCloseButton: true,
                    data: {
                        action: ManageUserAction.Update,
                        personKey: personDetail.personKey,
                        internalCompanyKey: this.companyKey,
                        isFromAdminPage: this.isAdmin,
                        clientDashboardExperiences: this.clientDashboardExperiences,
                        isLsqAdminCompany: this.isLsqAdminCompany
                    },
                    isSubmitting$: this._isUpdatingClaimsSubject.asObservable(),
                    onOkClick: async () => {
                        this._isUpdatingClaimsSubject.next(true);
                        await this._manageUsersService.updateClaims(this.companyKey);
                        this._isUpdatingClaimsSubject.next(false);
                        this._listPeopleDetailForClientService.execute(
                            this._listPeopleDetailForClientRequest
                        );
                        this._modalService.closeModal();
                        this._alertService.success(this.updateSuccessMessage);
                    }
                })
            );
        } else {
            if (!this.isAdmin) {
                await this.router.navigateByUrl(getRouterLinkUrlById(atlas, SETTINGS_PROFILE));
            }
        }
    }

    public async activateConfirmDialog(personDetail: PersonDetail): Promise<void> {
        this._modalService.showModal(
            new Modal({
                id: 'activate-user',
                title: `${this.reactivatePrefixDetailsText} ${personDetail.firstName} ${personDetail.lastName}${this.reactivateSuffixDetailsText}`,
                component: PermissionsModalComponent,
                width: '65.5rem',
                showCloseButton: true,
                data: {
                    action: ManageUserAction.Activate,
                    personKey: personDetail.personKey,
                    internalCompanyKey: this.companyKey,
                    isFromAdminPage: this.isAdmin,
                    clientDashboardExperiences: this.clientDashboardExperiences,
                    isLsqAdminCompany: this.isLsqAdminCompany
                },
                isSubmitting$: this._isUpdatingClaimsSubject.asObservable(),
                onOkClick: async () => {
                    this._isUpdatingClaimsSubject.next(true);
                    await this._manageUsersService.updateClaims(this.companyKey);
                    await this._manageUsersService.setUserActiveState(
                        personDetail,
                        this.companyKey,
                        true
                    );
                    this._isUpdatingClaimsSubject.next(false);
                    this._modalService.closeModal();
                    this.refreshUserList();
                }
            })
        );
    }

    public async deactivateConfirmDialog(personDetail: PersonDetail): Promise<void> {
        this._modalService.showModal(
            new Modal({
                id: 'modify-user-permissions',
                title: `${this.deactivatePrefixDetailsText} ${personDetail.firstName} ${personDetail.lastName}${this.deactivateSuffixDetailsText}`,
                component: PermissionsModalComponent,
                width: '65.5rem',
                showCloseButton: true,
                data: {
                    action: ManageUserAction.Deactivate,
                    personKey: personDetail.personKey,
                    internalCompanyKey: this.companyKey,
                    isFromAdminPage: this.isAdmin,
                    clientDashboardExperiences: this.clientDashboardExperiences,
                    isLsqAdminCompany: this.isLsqAdminCompany
                },
                isSubmitting$: this._isUpdatingClaimsSubject.asObservable(),
                onOkClick: async () => {
                    this._isUpdatingClaimsSubject.next(true);
                    await this._manageUsersService.setUserActiveState(
                        personDetail,
                        this.companyKey,
                        false
                    );
                    this._isUpdatingClaimsSubject.next(false);
                    this._modalService.closeModal();
                    this.refreshUserList();
                }
            })
        );
    }

    public refreshUserList(): void {
        this.isFetchingUserDetails = true;
        this._listPeopleDetailForClientService.execute(
            new ListPeopleDetailForClientRequest(this._listPeopleDetailForClientRequest)
        );
    }

    public addNewUser(): void {
        // tslint:disable-next-line
        this._manageUsersService.addUser(
            this.companyKey,
            this.isAdmin,
            this.clientDashboardExperiences,
            this.isLsqAdminCompany
        );
        const addUserDetailsModal = new AddUserPersonalDetailsModal({
            title: this._modalTitle,
            data: { isProspectInvite: this.isProspectView }
        });
        const addUserPermissionsModal = new AddUserPermissionsModal({
            title: this._modalTitle,
            data: { isProspectInvite: this.isProspectView }
        });
        this._modalWorkflowService.showWorkflow([addUserDetailsModal, addUserPermissionsModal]);
        this._modalWorkflowService.hideWorkflowStream.pipe(first()).subscribe(() => {
            this.refreshUserList();
        });
    }

    public impersonate(personDetail: PersonDetail): void {
        if (this.companyKey) {
            this.impersonateUser.emit({
                companyKey: this.companyKey,
                userKey: personDetail.userKey
            });
        }
    }

    public async reinviteUser(userKey: Guid): Promise<void> {
        await this._manageUsersService.reinviteUser(userKey);
        this._alertService.success('An invitation has been sent!');
    }

    public showAddUserButton(): boolean {
        return (
            !this.isAdmin ||
            this.clientDashboardExperiences.includes(DashboardExperience.Factoring) ||
            this.showActiveUsers
        );
    }

    public async sendInvite(): Promise<void> {
        let error: any;
        try {
            if (this.isProspectView) {
                await this._manageUsersService.createUserForProspect();
            } else {
                await this._manageUsersService.createUserForClient();
            }
        } catch (_error) {
            error = _error;
            if (error?.code === ResponseCode.Conflict) {
                // Duplicate user.
                this._alertService.error(
                    this._i18n({
                        value:
                            'This email has already been used to invite a user to your organization.'
                    })
                );
            } else {
                this._alertService.error(
                    this._i18n({
                        value: 'Failed to create the user. Please refresh the page and try again.'
                    })
                );
            }
        }
        if (error) {
            throw error;
        }
    }

    public get canManageAdminCompanies(): boolean {
        return (
            (this.companyKey === environment.alanRuudCompanyKey ||
                this.companyKey === environment.lsqAdminCompanyKey) &&
            this._manageUsersService.isAdminClaims()
        );
    }

    public get isNotAdminCompany(): boolean {
        return (
            this.companyKey !== environment.alanRuudCompanyKey &&
            this.companyKey !== environment.lsqAdminCompanyKey
        );
    }
}
